<template>
  <div class="data-table__wrapper">
    <filter-tags v-if="filterable"
                 :fields="filterFields"
                 :entity="entity"
                 @change="onFiltersChange"></filter-tags>
    <card :class="{'card-plain': plain}" card-body-classes="p-0" :header-classes="plain ? 'px-0': undefined">
      <template v-slot:header v-if="showHeader">
        <div class="flex justify-end mb-2">
          <div class="table-header-left flex items-center" v-if="$slots['table-header-left']">
            <slot name="table-header-left">
            </slot>
          </div>
          <div class="table-title items-center flex" v-if="title">
            <h5 class="font-medium text-lg">{{title}}</h5>
          </div>
          <div class="table-search" v-if="searchable">
            <fg-input class="input-sm"
                      :placeholder="$t('generic.search')"
                      v-model="searchQuery"
                      @input="onSearchChange"
                      @addon-right-click="onSearchIconClick"
                      :addon-right-icon="searchQuery ? 'nc-icon nc-simple-remove' : 'nc-icon nc-zoom-split'">
            </fg-input>
          </div>
          <div class="table-filters">
            <slot name="filter-buttons">
              <base-button v-if="includeFilters" icon outline @click="onFilterClick">
                <i class="fas fa-filter"></i>
              </base-button>
              <base-button v-if="include('add')"
                           icon
                           outline
                           @click="onAdd">
                <i class="fas fa-plus"></i>
              </base-button>
              <base-button v-if="include('refresh')"
                           icon
                           outline
                           type="info"
                           @click="refresh">
                <i class="fas fa-sync-alt"></i>
              </base-button>
            </slot>
            <slot name="extra-filter-buttons" :last-query="lastQuery">

            </slot>
          </div>
        </div>
        <fade-transition :duration="200">
          <div v-show="showFilters" class="filters-row">
            <slot name="filters" :show="showFilters"></slot>
          </div>
        </fade-transition>
      </template>
      <el-table
        v-loading="(loading || isLoading) && tableData.length"
        element-loading-background="transparent"
        :class="{'table-clickable': clickable || goToDetailsOnRowClick}"
        :data="tableData"
        :row-class-name="tableRowClassName"
        :default-sort="defaultSort"
        v-bind="$attrs"
        v-on="listeners"
        ref="table"
        style="width: 100%">
        <template v-slot:empty>
          <div class="w-full flex justify-center items-center py-3 text-lg tracking-wider font-medium">
            <template v-if="loading || isLoading">
              <i class="el-icon-loading mr-1"></i> {{$t('generic.loading')}}
            </template>
            <template v-else>{{$t('generic.noData')}}</template>
          </div>
        </template>
        <slot name="columns">
          <el-table-column
            v-if="selectable"
            type="selection"
            align="center"
            width="50">
          </el-table-column>
          <el-table-column
            v-for="column in columns"
            :sortable="column.sortable ? 'custom' : isSortable(column)"
            :key="column.prop"
            v-bind="column"
            :min-width="column.minWidth || '150px'">
            <template v-slot:default="{row, $index}">
              <slot :name="column.prop || column.type || column.label"
                    :row="row"
                    :index="$index"
                    :sortable="isSortable(column)">
                {{row[column.prop]}}
              </slot>
            </template>
          </el-table-column>
        </slot>
        <slot name="operations">
          <el-table-column v-if="showOperationsColumn"
                           fixed="right"
                           min-width="125px"
                           :align="operationsAlign">
            <div slot-scope="{row, $index }" class="table-buttons">
              <slot name="extra-buttons" :row="row" :index="$index"></slot>
              <tooltip-icon-button v-if="include('edit')"
                                   icon="far fa-edit"
                                   :content="$t('generic.edit')"
                                   :to="{
                                    name: getRouteName(routeTypes.DETAILS, entity),
                                    params: {id: row[rowNavigationKey] || row.id}
                                   }"
                                   @click.stop="onEdit(row, $index)">
              </tooltip-icon-button>
              <tooltip-icon-button v-if="include('delete')"
                                   :content="$t('generic.delete.title')"
                                   icon="far fa-trash-alt"
                                   type="danger"
                                   @click.stop="onDelete(row, $index)">
              </tooltip-icon-button>
            </div>
          </el-table-column>
        </slot>
      </el-table>
      <slot name="pagination" :page="page" :total="total">
        <div class="d-flex justify-content-end"
             :class="{'mt-6 mb-3': showPagination}">
          <el-pagination
            v-show="showPagination && tableData.length > 0"
            @size-change="getTableData"
            @current-change="getTableData"
            :current-page.sync="page"
            :page-sizes="perPageOptions"
            :page-size="perPage"
            :pager-count="5"
            layout="total, slot, prev, pager, next, jumper"
            :total="total">
            <el-select class="select-default"
                       size="mini"
                       @change="getTableData(1)"
                       v-model="perPage">
              <el-option v-for="value in perPageOptions"
                         class="select-default"
                         :key="value"
                         :label="value"
                         :value="value">
              </el-option>
            </el-select>
          </el-pagination>
        </div>
      </slot>
    </card>
  </div>
</template>

<script>
  import debounce from 'lodash/debounce';
  import TooltipIconButton from 'src/components/Buttons/TooltipIconButton'
  import CollectingIcon from 'src/components/Table/CollectingIcon'
  import { Table, TableColumn, Select, Option, Pagination } from 'element-ui';
  import { getRouteName, entities, routeTypes } from 'src/config/routeTypes';
  import { getSortParamsFromString, getSortParamsFromArray } from './DataTable/getSortParams';
  import FilterTags from './Filters/FilterTags'
  import SearchService from 'src/services/SearchService'
  import get from 'lodash/get'
  import LanguageSelect from 'src/components/LanguageSelect'
  export default {
    name: "data-table",
    inheritAttrs: false,
    components: {
      FilterTags,
      LanguageSelect,
      CollectingIcon,
      TooltipIconButton,
      [Table.name]: Table,
      [TableColumn.name]: TableColumn,
      [Select.name]: Select,
      [Option.name]: Option,
      [Pagination.name]: Pagination,
    },
    props: {
      columns: {
        type: Array,
        default: () => [],
        required: true
      },
      data: {
        type: Array,
        default: () => []
      },
      localData: {
        type: Boolean,
        default: false
      },
      selectable: {
        type: Boolean,
        default: false
      },
      includeFilters: {
        type: Boolean,
        default: false
      },
      clickable: {
        type: Boolean,
        default: true
      },
      isLoading: {
        type: Boolean,
        default: false
      },
      plain: {
        type: Boolean,
        default: false
      },
      filterable: {
        type: Boolean,
        default: false
      },
      searchable: {
        type: Boolean,
        default: true
      },
      allowApiDelete: {
        type: Boolean,
        default: true
      },
      title: {
        type: String
      },
      operations: {
        type: String,
        default: `edit,delete,add,refresh`
      },
      service: {
        type: [Function, Object]
      },
      entity: {
        type: String,
        required: true
      },
      operationsAlign: {
        type: String,
        default: 'center'
      },
      filters: {
        type: Array,
        default: () => [] // [{ key: 'name', value: 'test'}]
      },
      goToDetailsOnRowClick: {
        type: Boolean,
        default: true
      },
      goToAddOnClick: {
        type: Boolean,
        default: true
      },
      showPagination: {
        type: Boolean,
        default: true
      },
      showConfirmBeforeDelete: {
        type: Boolean,
        default: true
      },
      syncRoute: {
        type: Boolean,
        default: true
      },
      rowNavigationKey: {
        type: String,
        default: 'uuid'
      },
      rowDeleteKey: {
        type: String,
        default: ''
      },
      disablePermissionChecks: {
        type: Boolean,
        default: false
      },
      serviceMethod: {
        type: String,
        default: 'get'
      },
      servicePath: {
        type: String,
        default: ''
      },
      serviceParams: {
        type: Object,
        default: () => ({})
      }
    },
    data() {
      let { page, sort, query } = this.$route.query;
      let sortParams = getSortParamsFromString(sort);
      if(!Object.keys(sortParams).length) {
        sortParams = getSortParamsFromArray(this.filters);
      }
      let vuexSearchQuery = get(this.$store.state.filters, `tableSearchQueries[${this.entity}]`, '')
      return {
        tagFilters: [],
        tableData: [],
        lastQuery: {},
        filterFields: {},
        page: page ? parseInt(page) : 1,
        total: 0,
        sort: sort || '',
        defaultSort: sortParams,
        loading: false,
        searchQuery: query || vuexSearchQuery || '',
        perPage: 20,
        perPageOptions: [5, 10, 15, 20, 25, 50],
        selectedRows: [],
        showFilters: false,
        routeTypes,
        debouncedGetData: null,
        selectedLocale: this.$i18n.locale,
      };
    },
    computed: {
      listeners() {
        return {
          ...this.$listeners,
          ["sort-change"]: this.onSortChange,
          ['selection-change']: this.handleSelectionChange,
          ['row-click']: this.onRowClick
        };
      },
      showHeader() {
        return this.title || this.searchable || this.$slots['table-header-left'] || this.searchable || this.operations !== '';
      },
      tableOperations() {
        return this.operations.split(',');
      },
      showOperationsColumn() {
        let hasBasicOperations = this.include('delete') || this.include('edit');
        let hasExtraButtons = this.$slots['extra-buttons'] || this.$scopedSlots['extra-buttons'];
        return hasBasicOperations || hasExtraButtons;
      },
      entityName() {
        return this.entity === entities.JOB_BOARD ? this.entity : `${this.entity}s`;
      },
      crudPermission() {
        return `admin-${this.entityName}-crud`;
      },
      viewPermission() {
        return `admin-${this.entityName}-view`;
      },
      canView() {
        if(this.disablePermissionChecks) {
          return true;
        }
        return this.$can(this.viewPermission)
      },
      canCRUD() {
        if(this.disablePermissionChecks) {
          return true;
        }
        return this.$can(this.crudPermission)
      },
    },
    methods: {
      getRouteName,
      async refresh() {
        await this.getTableData();
      },
      onFiltersChange(newFilters) {
        this.tagFilters = newFilters
        this.getTableData()
      },
      isSortable(column) {
        if (!this.filterable || !this.filterFields.filters) {
          return false
        }
        return this.filterFields.filters.find(f => f.field === column.prop) !== undefined
      },
      async getTableData(page) {
        try {
          this.loading = true
          let response = await SearchService.searchData({
            page,
            perPage: this.perPage,
            data: this.data,
            localData: this.localData,
            searchQuery: this.searchQuery,
            columns: this.columns,
            service: this.service,
            sort: this.sort,
            serviceMethod: this.serviceMethod,
            servicePath: this.servicePath,
            showPagination: this.showPagination,
            filterable: this.filterable,
            filters: this.filters,
            tagFilters: this.tagFilters,
            serviceParams: this.serviceParams,
            entity: this.entity
          })
          this.lastQuery = response.query
          this.tableData = response.tableData;
          this.total = response.total;
          this.sort = response.sort || this.sort
          this.filterFields = response.filterFields
          if(response.tableData.length === 0) {
            this.$emit('no-data');
          }
          if (this.syncRoute && !this.filterable) {
            const resolvedRoute = this.$router.resolve({ name: this.$route.name, query: response.params, hash: this.$route.hash });
            if (resolvedRoute.route.fullPath !== this.$route.fullPath) {
              this.$router.replace(resolvedRoute.route.fullPath)
            }
          }
          this.$emit('fetch-data', {
            data: response.tableData,
            total: response.total
          });
        } catch (e) {
          console.warn(e);
        } finally {
          this.loading = false;
        }
      },
      tableRowClassName({ row, rowIndex }) {
        if (row.selected) {
          return 'table-info';
        }
        return '';
      },
      include(operation) {
        let includesOperation = this.tableOperations.includes(operation);
        let crud = ['add', 'delete', 'edit'];
        if (!crud.includes(operation)) {
          return includesOperation;
        }
        return this.canCRUD && includesOperation;
      },
      onSortChange({ column, prop, order }) {
        if (prop !== null) {
          let sortOrder = order === "ascending" ? "+" : "-";
          this.sort = [`${sortOrder}${prop}`].join(',');
        } else {
          this.sort = '';
        }
        this.getTableData();
      },
      onSearchChange(val) {
        this.$emit('on-search-change', val);
        this.debouncedGetData();
        this.$store.commit('filters/SET_SEARCH_QUERY', {
          entity: this.entity,
          value: val
        })
      },
      onSearchIconClick() {
        this.searchQuery = ''
        this.onSearchChange('')
      },
      handleSelectionChange(selectedRows) {
        this.selectedRows = selectedRows;
        this.$emit('selection-change', selectedRows);
      },
      onAdd() {
        this.$emit('on-add');
        if (this.goToAddOnClick) {
          this.$router.push({ name: getRouteName(routeTypes.CREATE, this.entity) })
        }
      },
      async onDelete (row, index) {
        this.$emit('on-delete', row, index)
        if (this.allowApiDelete && (row[this.rowNavigationKey] || row.id)) {
          await this.beforeDelete(row)
          let id = row[this.rowDeleteKey] || row[this.rowNavigationKey] || row.id
          await this.service.delete(id)
        }
        this.tableData.splice(index, 1)
      },
      async beforeDelete(row) {
        let fieldsToCheck = ['title', 'name', 'fullName', 'given_name', 'provider_domain', 'uuid'];
        if (!this.showConfirmBeforeDelete) {
          return;
        }
        let fieldData = row[this.rowNavigationKey] || row.id;
        let field = 'id';
        for (let key of fieldsToCheck) {
          let value = row[key];
          if (value) {
            fieldData = value;
            field = key;
            break;
          }
        }
        let msg = this.$t('generic.delete.confirmTableMessage', {
          field,
          id: fieldData,
          entity: this.entity
        });
        await this.$confirm(msg, { dangerouslyUseHTMLString: true });
      },
      onEdit (row) {
        this.$emit('on-edit', row)
        if (!this.goToDetailsOnRowClick) {
          return
        }
        this.navigateToDetails(row)
      },
      navigateToDetails(row) {
        this.$router.push({
          name: getRouteName(routeTypes.DETAILS, this.entity),
          params: {id: row[this.rowNavigationKey] || row.id}
        })
      },
      onRowClick (row, column, event) {
        this.$emit('on-row-click', row, event, column)
        if (this.goToDetailsOnRowClick && this.canView) {
          this.$router.push({
            name: getRouteName(routeTypes.DETAILS, this.entity), params:
              {id: row[this.rowNavigationKey] || row.id}
          })
        }
      },
      onFilterClick() {
        this.$emit('on-filter-click');
        this.showFilters = !this.showFilters;
      },
      addExtraClasses() {
        if (this.showPagination) {
          let paginationInput = document.querySelector('.el-pagination__jump');
          paginationInput.classList.add('form-group');
        }
      }
    },
    mounted() {
      this.debouncedGetData = debounce(this.getTableData, 200)
      this.getTableData();
      this.addExtraClasses();
    },
    watch: {
      '$store.state.config.textLanguage'() {
        this.refresh()
      },
      filters() {
        this.getTableData();
      },
      data() {
        this.getTableData();
      }
    }
  };
</script>

<!-- Add "scoped" attribute to limit CSS to this component only -->
<style lang="scss">
  .data-table__wrapper .el-loading-spinner .path {
    stroke: currentColor;
    @apply text-gray-700;
  }
  .data-table__wrapper .el-loading-spinner {
    margin-top: 5px;
  }
  .data-table__wrapper .table-search .invalid-feedback {
    display: none !important;
  }
</style>
